import { Download, Copy, Globe } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';
import { copyWithToast } from '../../utils/clipboard';

interface TranslateDocumentDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function TranslateDocumentDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: TranslateDocumentDisplayProps) {
  // Check if this result contains translation data
  if (!result || typeof result !== 'object') return null;

  const isTranslation = result.translated_sections !== undefined ||
                        result.claims !== undefined ||
                        result.description !== undefined ||
                        result.abstract !== undefined ||
                        result.title !== undefined ||
                        result.translated_text !== undefined ||
                        result.download_url !== undefined; // File translation result

  if (!isTranslation) return null;

  // Check if this is a file translation result (has download_url)
  const isFileTranslation = result.download_url !== undefined;

  const copyToClipboard = (text: string) => copyWithToast(text, showToast);

  const downloadSection = async (content: string, sectionName: string) => {
    try {
      const displayName = sectionName.charAt(0).toUpperCase() + sectionName.slice(1).replace(/_/g, ' ');
      const children: Paragraph[] = [];

      // Section title
      children.push(
        new Paragraph({
          text: displayName,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Section content (split by paragraphs)
      const paragraphs = content.split('\n\n');
      paragraphs.forEach((para) => {
        if (para.trim()) {
          children.push(
            new Paragraph({
              text: para.trim(),
              spacing: { after: 200, line: 360 },
            })
          );
        }
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sectionName}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast?.(`${displayName} downloaded as DOCX!`);
    } catch (error) {
      console.error('Error creating section DOCX:', error);
      showToast?.('Error downloading section');
    }
  };

  const downloadAll = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Translated Patent Document",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Add translation metadata if available
      if (result.source_language || result.target_language) {
        const metadataParts = [];
        if (result.source_language) {
          metadataParts.push(new TextRun({ text: 'Source Language: ', bold: true }));
          metadataParts.push(new TextRun({ text: `${result.source_language}  ` }));
        }
        if (result.target_language) {
          metadataParts.push(new TextRun({ text: 'Target Language: ', bold: true }));
          metadataParts.push(new TextRun({ text: result.target_language }));
        }

        children.push(
          new Paragraph({
            children: metadataParts,
            spacing: { after: 300 },
          })
        );
      }

      // Handle translated_sections if present
      if (result.translated_sections && typeof result.translated_sections === 'object') {
        Object.entries(result.translated_sections).forEach(([sectionName, content]) => {
          const displayName = sectionName.charAt(0).toUpperCase() + sectionName.slice(1).replace(/_/g, ' ');
          const contentStr = typeof content === 'string' ? content : JSON.stringify(content, null, 2);

          // Section heading
          children.push(
            new Paragraph({
              text: displayName,
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 300, after: 200 },
            })
          );

          // Section content (split by paragraphs)
          const paragraphs = contentStr.split('\n\n');
          paragraphs.forEach((para) => {
            if (para.trim()) {
              children.push(
                new Paragraph({
                  text: para.trim(),
                  spacing: { after: 200, line: 360 },
                })
              );
            }
          });
        });
      } else if (result.translated_text) {
        // Handle simple translated_text format
        if (result.original_text) {
          children.push(
            new Paragraph({
              text: "Original Text",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 300, after: 200 },
            }),
            new Paragraph({
              text: result.original_text,
              spacing: { after: 300, line: 360 },
            })
          );
        }

        children.push(
          new Paragraph({
            text: "Translated Text",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          }),
          new Paragraph({
            text: result.translated_text,
            spacing: { after: 200, line: 360 },
          })
        );
      } else {
        // Handle individual fields
        const sections = [
          { name: 'Title', content: result.title },
          { name: 'Abstract', content: result.abstract },
          { name: 'Claims', content: result.claims },
          { name: 'Description', content: result.description }
        ];

        sections.forEach(({ name, content }) => {
          if (content) {
            const contentStr = typeof content === 'string' ? content : JSON.stringify(content, null, 2);

            children.push(
              new Paragraph({
                text: name,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 200 },
              })
            );

            const paragraphs = contentStr.split('\n\n');
            paragraphs.forEach((para) => {
              if (para.trim()) {
                children.push(
                  new Paragraph({
                    text: para.trim(),
                    spacing: { after: 200, line: 360 },
                  })
                );
              }
            });
          }
        });
      }

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'translated_patent_document.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast?.('Full document downloaded as DOCX!');
    } catch (error) {
      console.error('Error creating DOCX:', error);
      showToast?.('Error downloading document');
    }
  };

  // Section colors for visual distinction
  const sectionColors: Record<string, { bg: string; border: string; text: string }> = {
    title: { bg: 'bg-purple-100', border: 'border-purple-200', text: 'text-purple-900' },
    abstract: { bg: 'bg-blue-100', border: 'border-blue-200', text: 'text-blue-900' },
    claims: { bg: 'bg-green-100', border: 'border-green-200', text: 'text-green-900' },
    description: { bg: 'bg-yellow-100', border: 'border-yellow-200', text: 'text-yellow-900' },
    background: { bg: 'bg-orange-100', border: 'border-orange-200', text: 'text-orange-900' },
    summary: { bg: 'bg-pink-100', border: 'border-pink-200', text: 'text-pink-900' },
    default: { bg: 'bg-gray-100', border: 'border-gray-200', text: 'text-gray-900' }
  };

  const getSectionColor = (sectionName: string) => {
    const normalized = sectionName.toLowerCase();
    return sectionColors[normalized] || sectionColors.default;
  };

  // Determine which sections to display
  const sections = result.translated_sections
    ? Object.entries(result.translated_sections)
    : result.translated_text
    ? [
        ...(result.original_text ? [['Original Text', result.original_text]] : []),
        ['Translated Text', result.translated_text]
      ]
    : [
        ...(result.title ? [['title', result.title]] : []),
        ...(result.abstract ? [['abstract', result.abstract]] : []),
        ...(result.claims ? [['claims', result.claims]] : []),
        ...(result.description ? [['description', result.description]] : [])
      ];

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold">Patent Document Translation</h3>
        </div>
      )}

      {/* File Translation Download Section */}
      {isFileTranslation && result.download_url && (
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-semibold text-sm text-green-900">Document Translation Complete</p>
                <p className="text-xs text-green-700 mt-1">
                  {result.output_filename || 'Translated document ready for download'}
                </p>
              </div>
            </div>
            <a
              href={result.download_url}
              download={result.output_filename || 'translated_document'}
              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
          </div>
          {/* Translation Metadata */}
          {(result.source_language || result.target_language || result.total_characters) && (
            <div className="mt-3 pt-3 border-t border-green-200 grid grid-cols-3 gap-4 text-sm">
              {result.source_language && (
                <div>
                  <span className="font-medium text-green-900">From:</span>{' '}
                  <span className="text-green-700">{result.source_language}</span>
                </div>
              )}
              {result.target_language && (
                <div>
                  <span className="font-medium text-green-900">To:</span>{' '}
                  <span className="text-green-700">{result.target_language}</span>
                </div>
              )}
              {result.total_characters && (
                <div>
                  <span className="font-medium text-green-900">Characters:</span>{' '}
                  <span className="text-green-700">{result.total_characters.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {/* Header with download all button */}
        {!isFileTranslation && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-sm text-blue-900">Translated Patent Document</span>
              </div>
              <button
                onClick={downloadAll}
                className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Download All
              </button>
            </div>
          </div>
        )}

        {/* Dynamic Sections - only show for non-file translations */}
        {!isFileTranslation && sections.map(([sectionName, content]: [string, any], idx: number) => {
          const colors = getSectionColor(sectionName);
          const displayName = sectionName.charAt(0).toUpperCase() + sectionName.slice(1).replace(/_/g, ' ');
          const contentStr = typeof content === 'string' ? content : JSON.stringify(content, null, 2);

          return (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className={`${colors.bg} px-4 py-2 border-b ${colors.border} flex items-center justify-between`}>
                <h4 className={`font-semibold text-sm ${colors.text}`}>{displayName}</h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(contentStr)}
                    className={`${colors.text} hover:opacity-80 p-1`}
                    title={`Copy ${displayName}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadSection(contentStr, sectionName)}
                    className={`${colors.text} hover:opacity-80 p-1`}
                    title={`Download ${displayName}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className={`p-4 ${colors.bg} bg-opacity-30`}>
                <div className={`text-sm whitespace-pre-wrap ${sectionName === 'title' ? 'font-medium' : ''} ${['claims', 'description'].includes(sectionName) ? 'max-h-96 overflow-y-auto' : ''}`}>
                  {contentStr}
                </div>
              </div>
            </div>
          );
        })}

        {/* Translation Metadata (if available) - only for non-file translations */}
        {!isFileTranslation && (result.source_language || result.target_language || result.word_count || result.character_count || result.total_characters || result.translation_notes) && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3">Translation Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {result.source_language && (
                <div>
                  <span className="font-medium">Source Language:</span> {result.source_language}
                </div>
              )}
              {result.target_language && (
                <div>
                  <span className="font-medium">Target Language:</span> {result.target_language}
                </div>
              )}
              {result.word_count && (
                <div>
                  <span className="font-medium">Word Count:</span> {result.word_count}
                </div>
              )}
              {(result.character_count || result.total_characters) && (
                <div>
                  <span className="font-medium">Character Count:</span> {result.character_count || result.total_characters}
                </div>
              )}
            </div>
            {result.translation_notes && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <span className="font-medium">Notes:</span>
                <p className="mt-1 text-sm whitespace-pre-wrap">{result.translation_notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
